// ─────────────────────────────────────────────────────────────────────────────
// LabThree — Guida "Prodotti e semilavorati"
// Centro assistenza → guida per amministratori, solo piano TRACE.
//
// Schermate RICOSTRUITE (vedi la nota in atomi.js). Le etichette fra virgolette
// sono copiate dal codice.
//
// Riferimenti nel codice, per chi dovrà aggiornarla:
//   Schermata ........ App.js, GestioneProdotti
//   Categorie ........ App.js, costante CAT_PRODOTTO — solo due valori:
//                      SEMILAVORATO e PRODOTTO_FINITO
//   Campi ............ un'unica sezione "Dati anagrafici": Nome *, Codice,
//                      Categoria, Unità di misura, Descrizione, Note
//   NOTA IMPORTANTE .. la scheda anagrafica del prodotto NON ha allergeni né
//                      valori nutrizionali. Quelli arrivano dagli ingredienti
//                      attraverso la ricetta (componente TechDataTab, usato da
//                      GestioneRicette). Se un giorno venissero aggiunti anche
//                      sul prodotto, il capitolo 4 va riscritto.
//   Semilavorati ..... in ricetta si agganciano con tr_ricette_ingredienti.
//                      id_semilavorato (tipo = 'SEMILAVORATO'); il consumo passa
//                      da tr_lavorazioni_sl → tr_magazzino_pf
// ─────────────────────────────────────────────────────────────────────────────

import React from 'react';
import LT from '../../ui/tokens';
import I, { Ico } from '../../ui/icons';
import {
  GuidaLayout, H, P, Strong, Code, Callout, FAQ,
} from './atomi';

const SEZIONI = [
  { id: 'p0', label: 'Prima di iniziare' },
  { id: 'p1', label: '1. I due tipi' },
  { id: 'p2', label: '2. Il semilavorato' },
  { id: 'p3', label: '3. Codice e unità di misura' },
  { id: 'p4', label: '4. Gli allergeni non sono qui' },
  { id: 'p5', label: 'Le trappole' },
  { id: 'p6', label: 'Domande frequenti' },
];

// Catena a due livelli: impasto → salame
const Catena = () => {
  const box = (titolo, sotto, bg, fg, bd) => (
    <div style={{
      flex: '1 1 150px', minWidth: 150, background: bg, border: `1px solid ${bd}`,
      borderRadius: LT.r.md, padding: '11px 10px', textAlign: 'center',
    }}>
      <div style={{ fontSize: 13, fontWeight: 800, color: fg }}>{titolo}</div>
      <div style={{ fontSize: 11, color: LT.n500, marginTop: 3, lineHeight: 1.35 }}>{sotto}</div>
    </div>
  );
  const freccia = (etichetta) => (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      justifyContent: 'center', flex: '0 0 auto', padding: '0 2px',
    }}>
      <Ico d={I.arrowRight} size={14} stroke={LT.n400} />
      <div style={{ fontSize: 9.5, color: LT.n400, fontWeight: 700, marginTop: 1 }}>{etichetta}</div>
    </div>
  );
  return (
    <div style={{
      display: 'flex', flexWrap: 'wrap', gap: 6, margin: '20px 0', padding: 16,
      background: LT.n50, border: `1px solid ${LT.n150}`, borderRadius: LT.r.lg,
    }}>
      {box('Carne, sale, spezie', 'ingredienti', LT.bluSoft, LT.blu, '#D8E6FE')}
      {freccia('1ª lavorazione')}
      {box('Impasto per salame', 'SEMILAVORATO', LT.arancioSoft, '#92400E', '#FDE9B5')}
      {freccia('2ª lavorazione')}
      {box('Salame Nostrano', 'PRODOTTO FINITO', LT.verdeSoft, '#166534', '#C7EFDA')}
    </div>
  );
};

export default function ProdottiSemilavorati() {
  return (
    <GuidaLayout
      titolo="Prodotti e semilavorati"
      sottotitolo="L'elenco di quello che producete. È l'anagrafica più breve del modulo, ma dentro c'è una distinzione che decide se la vostra tracciabilità arriva fino in fondo."
      durata="~ 8 minuti"
      destinatario="Per amministratori · piano TRACE"
      sezioni={SEZIONI}
    >

      {/* p0 */}
      <H id="p0">Prima di iniziare</H>

      <P>
        Se gli ingredienti sono quello che entra, i prodotti sono quello che esce. La scheda ha un
        blocco solo — nome, codice, categoria, unità di misura, descrizione e note — e l'unico
        campo obbligatorio è il <Strong>Nome</Strong>. Dieci secondi per prodotto.
      </P>
      <P>
        La sostanza di questo capitolo non sta nei campi, sta nella <Strong>categoria</Strong>:
        sono due sole, e sceglierla bene è quello che permette di ricostruire una catena su più
        passaggi.
      </P>

      {/* p1 */}
      <H id="p1" cat="produzione">1. I due tipi</H>

      <P>
        <Strong>PRODOTTO FINITO</Strong> è quello che vendete: esce dall'ultima lavorazione, entra
        nel magazzino prodotti finiti e da lì parte con una bolla di uscita verso un cliente.
      </P>
      <P>
        <Strong>SEMILAVORATO</Strong> è quello che producete per usarlo voi, in un'altra
        lavorazione. Non lo vendete: lo consumate.
      </P>

      {/* p2 */}
      <H id="p2">2. Il semilavorato</H>

      <P>
        È il concetto che vale la pena capire una volta per tutte, perché è quello che distingue
        una tracciabilità completa da una che si ferma a metà. Un esempio da salumificio:
      </P>

      <Catena />

      <P>
        L'impasto è un prodotto a tutti gli effetti — ha un nome, un lotto, una quantità, sta in
        un'ubicazione — ma la sua vita è interna. Nasce da una lavorazione che consuma carne, sale
        e spezie, e alimenta una seconda lavorazione che produce il salame.
      </P>
      <P>
        Dichiararlo come semilavorato serve a questo: quando un giorno partite dal lotto di salame
        e chiedete al programma da dove viene, lui risale al lotto di impasto, e{' '}
        <Strong>da quello ai lotti di carne del fornitore</Strong>. Due salti invece di uno. Se
        invece registrate tutto come un'unica lavorazione, il legame intermedio non esiste e la
        catena si accorcia.
      </P>

      <Callout kind="tip" titolo="Serve un semilavorato per ogni fase?">
        No, e sarebbe un errore. Le <em>fasi</em> di una lavorazione — impasto, insacco,
        stagionatura — non sono semilavorati: stanno dentro la stessa lavorazione. Un semilavorato
        serve quando il prodotto intermedio <Strong>si ferma, prende un lotto suo, e viene ripreso
        in un momento diverso</Strong>, magari per farne prodotti differenti. Se dallo stesso
        impasto tirate fuori salami e salamini, quello è un semilavorato. Se l'impasto passa
        direttamente all'insacco nella stessa giornata e nella stessa lavorazione, sono due fasi.
      </Callout>

      {/* p3 */}
      <H id="p3">3. Codice e unità di misura</H>

      <P>
        Il <Strong>Codice</Strong> è libero e facoltativo: serve se in azienda avete già una vostra
        codifica dei prodotti, e la ricerca in cima all'elenco cerca sia per nome sia per codice —
        il campo dice "Cerca per nome o codice…". Se non usate codici, lasciatelo vuoto senza
        problemi.
      </P>
      <P>
        L'<Strong>unità di misura</Strong> è la stessa scelta fra otto che avete visto per gli
        ingredienti, e vale lo stesso avvertimento: si porta dietro le quantità prodotte, le
        giacenze e le bolle di uscita. Il criterio qui è <Strong>come vendete</Strong>: un salame
        che va a peso è in kg, uno che si vende a unità è in pz. Molti prodotti hanno senso in
        entrambi, e infatti nelle lavorazioni si possono dichiarare pezzi e chili insieme.
      </P>

      {/* p4 */}
      <H id="p4" cat="pulizie">4. Gli allergeni non sono qui</H>

      <P>
        Una cosa che sorprende chi arriva da questa schermata cercandoli: nella scheda del prodotto{' '}
        <Strong>non ci sono né gli allergeni né i valori nutrizionali</Strong>. Non è una
        mancanza, è la conseguenza di come funziona il sistema.
      </P>
      <P>
        Gli allergeni di un prodotto <Strong>non si dichiarano: si ereditano</Strong>. Li avete
        spuntati sugli ingredienti, la ricetta dice quali ingredienti compongono quel prodotto, e
        da lì risalgono. Dichiararli anche qui vorrebbe dire poter scrivere due verità diverse
        nello stesso programma, e la peggiore delle due finirebbe su un'etichetta.
      </P>
      <P>
        I dati tecnici e nutrizionali del prodotto finito si compilano nella{' '}
        <Code>Ricetta</Code>, nella scheda <Strong>Dati tecnici &amp; Nutrizionali</Strong>. È il
        posto giusto perché è lì che si sa di cosa è fatto.
      </P>

      {/* p5 */}
      <H id="p5">Le trappole</H>

      <div style={{ margin: '16px 0' }}>
        {[
          ['Confondere semilavorato e fase',
            'Le fasi stanno dentro una lavorazione. Il semilavorato è un prodotto che si ferma, prende un lotto suo e viene ripreso più tardi, anche per prodotti diversi.'],
          ['Registrare tutto come un unico prodotto finito',
            'Funziona, ma accorcia la catena: partendo dal prodotto non si risale al passaggio intermedio. Se in azienda un intermedio esiste davvero, conviene dichiararlo.'],
          ["L'unità di misura si trascina dietro le quantità",
            'Come per gli ingredienti: quantità prodotte, giacenze e bolle di uscita parlano in quella unità, e cambiarla dopo non converte i numeri già scritti.'],
          ['Cercare gli allergeni in questa schermata',
            'Non ci sono, e non è un difetto: si ereditano dagli ingredienti attraverso la ricetta.'],
        ].map(([t, d]) => (
          <div key={t} style={{
            display: 'flex', gap: 11, alignItems: 'flex-start',
            padding: '11px 0', borderBottom: `1px solid ${LT.n150}`,
          }}>
            <Ico d={I.alert} size={16} stroke={LT.arancio} style={{ marginTop: 3, flexShrink: 0 }} />
            <div>
              <div style={{ fontSize: 14.5, fontWeight: 700, color: LT.n900 }}>{t}</div>
              <div style={{ fontSize: 13.5, color: LT.n600, lineHeight: 1.55, marginTop: 2 }}>{d}</div>
            </div>
          </div>
        ))}
      </div>

      {/* p6 */}
      <H id="p6">Domande frequenti</H>

      <FAQ q="Un semilavorato lo posso anche vendere?">
        Nella pratica capita — un impasto venduto a un altro laboratorio. Il programma non lo
        impedisce, ma il posto naturale delle bolle di uscita sono i prodotti finiti. Se un
        intermedio lo vendete abitualmente, conviene inserirlo come prodotto finito e trattarlo
        come tale.
      </FAQ>

      <FAQ q="Devo inserire un prodotto per ogni formato in cui lo vendo?">
        Dipende da quanto i formati sono diversi. Se il contenuto è lo stesso e cambia solo la
        pezzatura, un prodotto solo con la quantità dichiarata in lavorazione basta. Se invece
        cambiano ricetta, etichetta o allergeni, sono prodotti diversi.
      </FAQ>

      <FAQ q="Il codice prodotto finisce sulle etichette o sui documenti?">
        Serve principalmente a voi per cercare in fretta nell'elenco. Quello che identifica una
        produzione nei documenti e nella tracciabilità è il <Strong>lotto</Strong>, non il codice
        del prodotto.
      </FAQ>

      <FAQ q="Posso cambiare un prodotto da semilavorato a prodotto finito?">
        Sì, la categoria si modifica. Ma se ci sono già lavorazioni che lo trattano come
        semilavorato, quelle restano come sono: la modifica vale da lì in avanti. Se il cambio
        riguarda il modo in cui lavorate, meglio creare il prodotto nuovo e lasciare il vecchio al
        suo storico.
      </FAQ>

      <div style={{
        marginTop: 40, padding: 18, background: LT.bluSoft,
        borderRadius: LT.r.lg, border: `1px solid ${LT.n150}`,
      }}>
        <div style={{ fontSize: 15.5, fontWeight: 700, color: LT.navy, marginBottom: 4 }}>
          E adesso?
        </div>
        <div style={{ fontSize: 14, color: LT.n700, lineHeight: 1.65 }}>
          Con ingredienti e prodotti a posto hai tutti i pezzi. Il passo successivo è la{' '}
          <Strong>ricetta</Strong>: è lì che si dice quali ingredienti, in che quantità e
          attraverso quali fasi si arriva al prodotto — ed è il lavoro che rende automatico tutto
          quello che viene dopo.
        </div>
      </div>

    </GuidaLayout>
  );
}
